import React, { useEffect, useRef } from 'react'
import LoadingSpinner from './LoadingSpinner'
import { reviewApi } from '../api/review'
import { dataURLtoFile, fileToBase64 } from '../utils/fileUtils'
import { downscaleForUpload } from '../utils/downscalePhoto'
import { toJpegIfHeif } from '../utils/heif'
import { isApp } from '../utils/platform'
import { toastUtil } from '../utils/toast'

export interface AttachedPhoto {
  id: string
  /** 화면에 그릴 미리보기(data URL) */
  preview: string
  /** 서버에 올린 주소. 올리는 중이면 undefined, 실패하면 null */
  url?: string | null
}

interface Props {
  photos: AttachedPhoto[]
  onChange: (updater: (prev: AttachedPhoto[]) => AttachedPhoto[]) => void
  max?: number
}

/**
 * 사진 첨부 줄 — 기록 화면(StoreReview)의 사진 칸과 같은 모양이다.
 * 추가 칸과 미리보기 모두 92×116 · 모서리 16 · 오른쪽 위 지우기.
 * (기록 화면은 미리보기가 124×155로 더 크지만, 여기서는 추가 칸과 크기를 맞춘다 — 2026-11-04 도연님)
 *
 * 기록 화면과 달리 자르기·순서 바꾸기·근처 사진 추천은 없다. 문의에 붙이는 사진은
 * '무엇이 문제인지 보여주기'라 손볼 일이 없다.
 *
 * 고르는 즉시 기록 사진과 같은 길(/reviews/photo)로 올린다. 보낼 때는 주소만 싣는다.
 */
const AttachPhotoRow: React.FC<Props> = ({ photos, onChange, max = 5 }) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const countRef = useRef(photos.length)
  countRef.current = photos.length

  const addDataUrls = async (items: { data: string; name?: string }[]) => {
    const room = max - countRef.current
    if (room <= 0) return
    const picked = items.slice(0, room)
    if (items.length > room) toastUtil.warning(`최대 ${max}장까지 첨부할 수 있어요`)
    for (const it of picked) {
      let preview = it.data
      try {
        const file = await toJpegIfHeif(dataURLtoFile(it.data, it.name || 'photo.jpg'))
        if (file.type === 'image/jpeg' && !it.data.startsWith('data:image/jpeg')) preview = await fileToBase64(file)
      } catch {
        toastUtil.error('사진을 불러오지 못했어요')
        continue
      }
      const id = `${Date.now()}-${Math.random()}`
      onChange((prev) => [...prev, { id, preview }])
      // 기다리지 않고 올린다 — 다 올라가기 전에 보내기를 누르면 화면이 기다리게 한다
      void (async () => {
        const url = await reviewApi.uploadPhoto(await downscaleForUpload(preview))
        onChange((prev) => prev.map((p) => (p.id === id ? { ...p, url: url ?? null } : p)))
        if (!url) toastUtil.error('사진을 올리지 못했어요. 지우고 다시 골라 주세요')
      })()
    }
  }

  // 앱 갤러리 콜백. 이 화면을 떠나면 걷어 낸다(기록 화면의 콜백을 덮지 않게).
  useEffect(() => {
    const bridge = (window as any).FlutterBridge
    if (!isApp() || !bridge) return
    return () => {
      delete bridge.onImagesSelected
      delete bridge.onGalleryError
    }
  }, [])

  const openPicker = () => {
    if (photos.length >= max) {
      toastUtil.warning(`최대 ${max}장까지 첨부할 수 있어요`)
      return
    }
    const bridge = (window as any).FlutterBridge
    if (isApp() && bridge?.openGallery) {
      bridge.onGalleryError = (d: { message?: string }) => toastUtil.error(d?.message ?? '사진을 불러오지 못했어요')
      bridge.onImagesSelected = (d: any) => {
        if (Array.isArray(d?.images) && d.images.length > 0) void addDataUrls(d.images)
      }
      // 남은 장수를 알려 고르는 단계에서 막는다(기록 화면과 같은 이유 — 메모리)
      bridge.openGallery({ remaining: max - photos.length })
      return
    }
    fileInputRef.current?.click()
  }

  const onFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    e.target.value = ''
    const items: { data: string; name: string }[] = []
    for (const f of files) {
      try {
        items.push({ data: await fileToBase64(f), name: f.name })
      } catch {
        /* 못 읽은 파일은 건너뛴다 */
      }
    }
    void addDataUrls(items)
  }

  return (
    <div className="flex gap-8px overflow-x-auto scrollbar-hide -mx-20px px-20px" style={{ overscrollBehaviorX: 'contain' }}>
      {photos.length < max && (
        <button
          type="button"
          onClick={openPicker}
          className="flex-shrink-0 w-92px h-116px rounded-16px border border-1px border-4B3621-20% bg-472410-5% flex items-center justify-center hover:opacity-70"
        >
          <img src="/images/photo-plus-glyph.png" alt="사진 추가" className="w-20px h-20px" />
        </button>
      )}
      {photos.map((p) => (
        <div key={p.id} className="relative flex-shrink-0 w-92px h-116px rounded-16px overflow-hidden bg-472410-5%">
          <img src={p.preview} alt="첨부한 사진" className="w-full h-full object-cover block" />
          {p.url === undefined && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
              <LoadingSpinner size={20} />
            </div>
          )}
          <button
            type="button"
            onClick={() => onChange((prev) => prev.filter((x) => x.id !== p.id))}
            className="absolute top-5px right-5px w-24px h-24px flex items-center justify-center hover:opacity-70"
          >
            <img src="/images/photo-close-icon.png" alt="삭제" className="w-full h-full object-contain" />
          </button>
        </div>
      ))}
      <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={onFiles} className="hidden" />
    </div>
  )
}

export default AttachPhotoRow
